import type { SlotsRequest, SessionDetail, SessionSummary } from '@features/customization-session/types';

import Routing from '@core/api/routing';
import { baseApi } from '@core/api/base-api';

const customizationSessionApi = baseApi.injectEndpoints({
  endpoints: (builder) => {
    /** An action returns the whole updated session: it replaces the cached one, and the list is reloaded. */
    const syncSession = async (
      id: number,
      dispatch: (action: unknown) => unknown,
      queryFulfilled: Promise<{ data: SessionDetail }>
    ) => {
      try {
        const { data } = await queryFulfilled;
        dispatch(customizationSessionApi.util.updateQueryData('getSession', id, () => data));
        dispatch(customizationSessionApi.endpoints.getSessions.initiate(undefined, { forceRefetch: true }));
      } catch {
        // The error is reported by the caller.
      }
    };

    return {
      getSessions: builder.query<SessionSummary[], void>({
        query: () => ({ url: Routing.generate('rest_workshop_sessions', {}, true) }),
        transformResponse: (response: { sessions: SessionSummary[] }) => response.sessions,
        providesTags: ['customizationSessionList'],
      }),
      getSession: builder.query<SessionDetail, number>({
        query: (id) => ({ url: Routing.generate('rest_workshop_session', { session: id }, true) }),
        providesTags: (_result, _error, id) => [{ type: 'customizationSession', id }],
      }),
      loadProducts: builder.mutation<SessionDetail, SlotsRequest>({
        query: ({ id, ...body }) => ({
          url: Routing.generate('rest_workshop_session_load', { session: id }, true),
          method: 'POST',
          body,
        }),
        onQueryStarted: ({ id }, { dispatch, queryFulfilled }) => syncSession(id, dispatch, queryFulfilled),
      }),
      startSession: builder.mutation<SessionDetail, number>({
        query: (id) => ({
          url: Routing.generate('rest_workshop_session_start', { session: id }, true),
          method: 'POST',
        }),
        onQueryStarted: (id, { dispatch, queryFulfilled }) => syncSession(id, dispatch, queryFulfilled),
      }),
      finishSession: builder.mutation<SessionDetail, number>({
        query: (id) => ({
          url: Routing.generate('rest_workshop_session_finish', { session: id }, true),
          method: 'POST',
        }),
        onQueryStarted: (id, { dispatch, queryFulfilled }) => syncSession(id, dispatch, queryFulfilled),
      }),
      /** The session comes back updated, or nothing (204) when the removal emptied, and so deleted, it. */
      removeProduct: builder.mutation<SessionDetail | null, { id: number; slot: number }>({
        query: ({ id, slot }) => ({
          url: Routing.generate('rest_workshop_session_remove_product', { session: id, slot }, true),
          method: 'DELETE',
        }),
        transformResponse: (response: SessionDetail | null) => response ?? null,
        onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(customizationSessionApi.util.updateQueryData('getSession', id, () => data));
            }
            dispatch(customizationSessionApi.endpoints.getSessions.initiate(undefined, { forceRefetch: true }));
          } catch {
            // The error is reported by the caller.
          }
        },
      }),
      /** Fetches (or fetches again) the PDF of one personalization; the session comes back updated. */
      fetchCustomizationPdf: builder.mutation<SessionDetail, { id: number; customization: number }>({
        query: ({ id, customization }) => ({
          url: Routing.generate('rest_workshop_session_customization_pdf', { session: id, customization }, true),
          method: 'POST',
        }),
        onQueryStarted: ({ id }, { dispatch, queryFulfilled }) => syncSession(id, dispatch, queryFulfilled),
      }),
      /** Fetches the PDF of every personalization that has none; `failed` counts those the shop does not have. */
      fetchSessionPdfs: builder.mutation<{ session: SessionDetail; failed: number }, number>({
        query: (id) => ({
          url: Routing.generate('rest_workshop_session_pdf', { session: id }, true),
          method: 'POST',
        }),
        onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            dispatch(customizationSessionApi.util.updateQueryData('getSession', id, () => data.session));
          } catch {
            // The error is reported by the caller.
          }
        },
      }),
      confirmPdf: builder.mutation<SessionDetail, number>({
        query: (id) => ({
          url: Routing.generate('rest_workshop_session_confirm_pdf', { session: id }, true),
          method: 'POST',
        }),
        onQueryStarted: (id, { dispatch, queryFulfilled }) => syncSession(id, dispatch, queryFulfilled),
      }),
      confirmExport: builder.mutation<SessionDetail, number>({
        query: (id) => ({
          url: Routing.generate('rest_workshop_session_confirm_export', { session: id }, true),
          method: 'POST',
        }),
        onQueryStarted: (id, { dispatch, queryFulfilled }) => syncSession(id, dispatch, queryFulfilled),
      }),
      /** The zip of the production files: fetched with the token (a plain link would not carry it), then saved by the caller. */
      downloadExport: builder.mutation<{ blob: Blob; filename: string }, number>({
        query: (id) => ({
          url: Routing.generate('rest_workshop_session_export', { session: id }, true),
          responseHandler: async (response) => {
            if (!response.ok) {
              return response.json();
            }
            const disposition = response.headers.get('content-disposition') ?? '';
            const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'export.zip';
            return { blob: await response.blob(), filename };
          },
        }),
      }),
      storeProducts: builder.mutation<SessionDetail, SlotsRequest>({
        query: ({ id, ...body }) => ({
          url: Routing.generate('rest_workshop_session_store', { session: id }, true),
          method: 'POST',
          body,
        }),
        onQueryStarted: ({ id }, { dispatch, queryFulfilled }) => syncSession(id, dispatch, queryFulfilled),
      }),
    };
  },
});

export const {
  useGetSessionQuery,
  useGetSessionsQuery,
  useLoadProductsMutation,
  useStartSessionMutation,
  useFinishSessionMutation,
  useStoreProductsMutation,
  useRemoveProductMutation,
  useFetchSessionPdfsMutation,
  useFetchCustomizationPdfMutation,
  useConfirmPdfMutation,
  useConfirmExportMutation,
  useDownloadExportMutation,
} = customizationSessionApi;
